import KiccEasyCardApi from '@apis/easyCard/KiccEasyCardApi';
import kiccEasyCardQueries from '@apis/queries/kiccEasyCardQueries';
import Loading from '@components/Loading';
import { AppScreen } from '@stackflow/plugin-basic-ui';
import { ActivityComponentType, useFlow } from '@stackflow/react';
import { orderActions } from '@store/order/orderSlice';
import { useQueryClient } from '@tanstack/react-query';
import { getTempOrder } from '@utils/restoreOrderUtils';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toast } from 'sonner';

declare module '@stackflow/config' {
    interface Register {
        EasyCarRegistrationActivity: {
            result:'success' | 'cancel' | 'fail';
            code?: string;
            message?: string;
        }
    }
}

const errorCodes = [
    'EASY_CARD_INVALID_CARD_NUMBER',
    'EASY_CARD_INVALID_EXPIRY',
    'EASY_CARD_INVALID_PASSWORD',
    'EASY_CARD_INVALID_OWNER_ID',
    'EASY_CARD_ISSUER_DECLINED',
    'EASY_CARD_REGISTRATION_LIMIT_EXCEEDED',
    'EASY_CARD_ALREADY_REGISTERED',
];

const EasyCarRegistrationActivity: ActivityComponentType<'EasyCarRegistrationActivity'> = ({ params }) => {
    const { result, message, code } = params;
    const { t } = useTranslation('easyCard');
    const { push, replace } = useFlow();
    const queryClient = useQueryClient();
    const dispatch = useDispatch();

    const getErrorMessage = () => {
        if (code && errorCodes.includes(code)) {
            return t(`register.errors.${code}`);
        }
        return message || t('register.fail');
    };

    const handleFailure = () => {
        replace('MainActivity', {}, { animate: false });
        push('EasyCardActivity', {});
        const errorMessage = getErrorMessage();
        setTimeout(() => {
            toast.error(errorMessage);
        }, 200);
    };

    const handleCancel = () => {
        replace('MainActivity', {}, { animate: false });
        push('EasyCardActivity', {});
        setTimeout(() => {
            toast.info(t('register.cancel'));
        }, 200);
    };

    const handleSuccess = async () => {
        try {
            const data = await KiccEasyCardApi.getCards();
            queryClient.setQueryData(kiccEasyCardQueries.list().queryKey, data);
            const tempOrder = await getTempOrder('EASY_CARD');
            if (tempOrder) {
                dispatch(orderActions.copy(tempOrder));
            }
            replace('MainActivity', {}, { animate: false });
            if (!data.isPinRegistered) {
                push('EasyCardActivity', { isInitialPinSetting: true }, { animate: false });
                if (tempOrder) {
                    push('OrderActivity', {}, { animate: false });
                }
                push('EasyCardPinActivity', {});
            } else {
                push('EasyCardActivity', {});
                if (tempOrder) {
                    push('OrderActivity', {});
                }
            }
        } catch {
            replace('MainActivity', {}, { animate: false });
            push('EasyCardActivity', {}, { animate: false });
        }
    };

    useEffect(() => {
        switch (result) {
        case 'fail':
            handleFailure();
            break;
        case 'success':
            handleSuccess();
            break;
        default:
            handleCancel();
        }
    }, [result]);
    return (
        <AppScreen>
            <Loading/>
        </AppScreen>
    );
};

export default EasyCarRegistrationActivity;
